import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection } from '@/shared/ui/components/form';
import Select from '@/shared/ui/components/form/inputs/Select';
import MultiSelect from '@/shared/ui/components/form/inputs/MultiSelect';
import {
  RENTAL_PAYMENT_FREQUENCY_OPTIONS,
  RENTAL_INCLUDED_EXPENSES_OPTIONS,
  ADMINISTRATION_TYPE_OPTIONS,
  CONDO_INCLUDED_EXPENSES_OPTIONS,
  CONDO_PAID_BY_OPTIONS,
} from './schema';
import type { PropertyFormValues } from './schema';

interface RentalDetailsSectionProps {
  form: UseFormReturn<PropertyFormValues>;
}

/**
 * Renders rental-specific and condominium-specific detail fields.
 * Only shown when the transaction type includes "Aluguel" or condominium is set.
 */
export function RentalDetailsSection({ form }: RentalDetailsSectionProps) {
  const { t } = useTranslation('features/properties');
  const transactionType = form.watch('transaction_type');
  const hasRental = transactionType?.includes('Aluguel');
  const isCondominium = form.watch('condominium');

  if (!hasRental && !isCondominium) return null;

  return (
    <FormSection title={t('form.sections.rentalDetails')}>
      <div className="flex flex-col gap-(--space-3)">
        {hasRental && (
          <>
            <Controller
              name="rental_payment_frequency"
              control={form.control}
              render={({ field }) => (
                <Select
                  label={t('form.drawer.rentalPaymentFrequency')}
                  placeholder={t('form.drawer.selectPlaceholder')}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  data={(RENTAL_PAYMENT_FREQUENCY_OPTIONS as readonly string[]).map(o => ({
                    value: o,
                    label: t(`form.options.rentalPaymentFrequency.${o}`),
                  }))}
                />
              )}
            />
            <Controller
              name="rental_included_expenses"
              control={form.control}
              render={({ field }) => (
                <MultiSelect
                  label={t('form.drawer.rentalIncludedExpenses')}
                  placeholder={t('form.drawer.selectPlaceholder')}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  data={(RENTAL_INCLUDED_EXPENSES_OPTIONS as readonly string[]).map(o => ({
                    value: o,
                    label: t(`form.options.rentalIncludedExpenses.${o}`),
                  }))}
                />
              )}
            />
            <Controller
              name="administration_type"
              control={form.control}
              render={({ field }) => (
                <Select
                  label={t('form.drawer.administrationType')}
                  placeholder={t('form.drawer.selectPlaceholder')}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  data={(ADMINISTRATION_TYPE_OPTIONS as readonly string[]).map(o => ({
                    value: o,
                    label: t(`form.options.administrationType.${o}`),
                  }))}
                />
              )}
            />
          </>
        )}
        {isCondominium && (
          <>
            <Controller
              name="condo_included_expenses"
              control={form.control}
              render={({ field }) => (
                <MultiSelect
                  label={t('form.drawer.condoIncludedExpenses')}
                  placeholder={t('form.drawer.selectPlaceholder')}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  data={(CONDO_INCLUDED_EXPENSES_OPTIONS as readonly string[]).map(o => ({
                    value: o,
                    label: t(`form.options.condoIncludedExpenses.${o}`),
                  }))}
                />
              )}
            />
            <Controller
              name="condo_paid_by"
              control={form.control}
              render={({ field }) => (
                <Select
                  label={t('form.drawer.condoPaidBy')}
                  placeholder={t('form.drawer.selectPlaceholder')}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  data={(CONDO_PAID_BY_OPTIONS as readonly string[]).map(o => ({
                    value: o,
                    label: t(`form.options.condoPaidBy.${o}`),
                  }))}
                />
              )}
            />
          </>
        )}
      </div>
    </FormSection>
  );
}
